import tw, { styled } from 'twin.macro'
import React, { Component } from 'react'
import Dropdown from './Dropdown'
import NavbarLink, { navbarLinkStyles } from './NavbarLink'
import NavbarSubLink, { navbarSubLinkStyles } from './NavbarSubLink'
import ButtonLink from './ButtonLink'
import { Link } from 'gatsby'
import Logo from '../img/logo-full.svg'

const Nav = styled('nav')`
  ${tw`flex justify-end justify-between flex-wrap p-4 sticky top-0 z-10`};

  ${props => props.sticking ? tw`shadow bg-white` : tw`bg-gray-50`}
`

const LogoWrapper = styled('div')`
  ${tw`flex justify-end flex-shrink-0 sm:mr-6`};
`

const NavList = styled('div')`
  ${tw`w-full lg:block flex-grow lg:flex lg:justify-end lg:w-auto items-center`};

  ${props => props.expanded ? tw`block` : tw`hidden`}
`

const ButtonsWrapper = styled('div')`
  ${tw`mt-1`};
`

const HamburgerContainer = styled('div')`
  ${tw`block lg:hidden cursor-pointer`}
`

const HamburgerButton = styled('button')`
  ${tw`flex justify-end px-3 py-2 border rounded text-gray-600 border-gray-700 hover:text-red-500 hover:border-red-500 cursor-pointer`}
`

const HamburgerIcon = styled('svg')`
  ${tw`fill-current h-3 w-3 cursor-pointer`}
`

const logoStyles = tw`h-10`;

const CURRENT_USER_STORAGE_KEY = 'logInState';

class Navbar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      customersDropdown: false,
      helpDropdown: false,
      featuresDropdown: false,
      hamburgerExpanded: false,
      currentUser: (typeof localStorage !== 'undefined' && localStorage.getItem(CURRENT_USER_STORAGE_KEY)) || null,
      navIsSticking: false
    }
  }

  componentDidMount() {
    this.refreshSession();
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const navIsSticking = window.scrollY > 50;

    if (navIsSticking !== this.state.navIsSticking) {
      this.setState({ navIsSticking });
    }
  }

  render() {
    return (
      <Nav sticking={this.state.navIsSticking}>
        <LogoWrapper>
          <Link to="/">
            <Logo css={logoStyles} />
          </Link>
        </LogoWrapper>
        <HamburgerContainer onClick={this.hamburgerClicked}>
          <HamburgerButton>
            <HamburgerIcon viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>Menu</title><path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z"/></HamburgerIcon>
          </HamburgerButton>
        </HamburgerContainer>
        <NavList expanded={this.state.hamburgerExpanded}>
          <NavbarLink to="/pricing/">Pricing</NavbarLink>
          <Dropdown title="Customers" showDropdown={this.state.customersDropdown} onDropdownToggle={this.toggleCustomersDropdown}>
            <NavbarSubLink to="/financial-services/">Financial Services</NavbarSubLink>
            <NavbarSubLink to="/real-estate-geocoding/">Real Estate</NavbarSubLink>
            <NavbarSubLink to="/geographic-data-for-journalists/">Journalism</NavbarSubLink>
            <NavbarSubLink to="/healthcare/">Healthcare</NavbarSubLink>
            <NavbarSubLink to="/political-and-civic-data/">Political, Civic & Non-Profit</NavbarSubLink>
            <NavbarSubLink to="/academia/">Academic & Research</NavbarSubLink>
            <NavbarSubLink to="/fleet-management-geocoding/">Fleet Management</NavbarSubLink>
            <NavbarSubLink to="/geo-analytics/">Data Analytics</NavbarSubLink>
            <NavbarSubLink to="/customers/">More…</NavbarSubLink>
          </Dropdown>
          <NavbarLink to="/compare/">Compare</NavbarLink>

          <Dropdown title="Product" showDropdown={this.state.featuresDropdown} onDropdownToggle={this.toggleFeaturesDropdown}>
            <NavbarSubLink to="/guides/">Overview</NavbarSubLink>
            <NavbarSubLink to="/features/api/">API</NavbarSubLink>
            <NavbarSubLink to="/features/spreadsheet/">Geocode Spreadsheets</NavbarSubLink>
            <NavbarSubLink to="/features/maps/">Geocodio Maps</NavbarSubLink>
          </Dropdown>

          <NavbarLink to="/security/">Security</NavbarLink>
          <NavbarLink to="/gdpr/">Privacy</NavbarLink>

          <Dropdown title="Help" showDropdown={this.state.helpDropdown} onDropdownToggle={this.toggleHelpDropdown}>
            <a css={navbarSubLinkStyles} href="/how-to-use-geocodio/">Tutorials</a>
            <a css={navbarSubLinkStyles} href="/guides/">Guides</a>
            <a css={navbarSubLinkStyles} href="/docs/">API Docs</a>
          </Dropdown>
          <a css={navbarLinkStyles} href="/contact/">Contact</a>
          <ButtonsWrapper>
            {this.renderSessionButtons()}
          </ButtonsWrapper>
        </NavList>
      </Nav>
    );
  }

  renderSessionButtons() {
    if (this.state.currentUser) {
      return <ButtonLink to={this.getDashboardUrl()} small special><i className="fa fa-user" /> {this.state.currentUser}</ButtonLink>
    } else {
      return [
        <ButtonLink key="login" to={`${this.getDashboardUrl()}/login`} small special>Log In</ButtonLink>,
        <ButtonLink key="signup" to={`${this.getDashboardUrl()}/register`} small bold>Sign Up</ButtonLink>
      ];
    }
  }

  hamburgerClicked = () => this.setState({hamburgerExpanded: !this.state.hamburgerExpanded});
  toggleCustomersDropdown = () => this.setState({customersDropdown: !this.state.customersDropdown, helpDropdown: false, featuresDropdown: false});
  toggleHelpDropdown = () => this.setState({helpDropdown: !this.state.helpDropdown, customersDropdown: false, featuresDropdown: false});
  toggleFeaturesDropdown = () => this.setState({featuresDropdown: !this.state.featuresDropdown, helpDropdown: false, customersDropdown: false});

  refreshSession() {
    const url = `${this.getDashboardUrl()}/api/v1/session`;

    fetch(url, { credentials: 'include' })
      .then(response => response.json())
      .then(response => {
        if (response && response.isAuthenticated && response.email) {
          localStorage.setItem(CURRENT_USER_STORAGE_KEY, response.email);
          this.setState({
            currentUser: response.email
          });
        } else {
          localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
          this.setState({
            currentUser: null
          });
        }
      })
      .catch(err => {});
  }

  getDashboardUrl() {
    const productionUrl = 'https://dash.geocod.io';
    const developmentUrl = 'https://dash.geocodio.dev';

    if (typeof window === 'undefined' ) {
      return productionUrl;
    }

    return window.location.href.indexOf('geocod.io') === -1
      ? developmentUrl
      : productionUrl;
  }
}

export default Navbar
